/**
 * KpiCard — shared KPI tile.
 *
 * accent      — colours BOTH card background and value text (legacy, ProduccionDiaria)
 * valueAccent — colours value text ONLY, card stays neutral
 * Both are optional; if neither is set the card uses the neutral style.
 */
export function KpiCard({
  label, value, sub, progress, progressColor, dark, accent, valueAccent,
}: {
  label: string
  value: string
  sub: string
  progress?: number
  progressColor?: string
  dark: boolean
  /** Changes card bg + value text */
  accent?: 'red' | 'amber'
  /** Changes value text only; ignored when accent is set */
  valueAccent?: 'red' | 'amber' | 'sky' | 'emerald'
}) {
  // Card background
  const cardCls = accent === 'red'
    ? (dark ? 'bg-red-950/25 border-red-500/30'   : 'bg-red-50/60 border-red-200')
    : accent === 'amber'
      ? (dark ? 'bg-amber-950/25 border-amber-500/30' : 'bg-amber-50/60 border-amber-200')
      : (dark ? 'bg-slate-900 border-white/8'         : 'bg-white border-slate-200')

  // Value text — accent takes precedence; fall through to valueAccent; then neutral
  const valueCls = accent === 'red'
    ? (dark ? 'text-red-400'     : 'text-red-600')
    : accent === 'amber'
      ? (dark ? 'text-amber-400'   : 'text-amber-600')
      : valueAccent === 'red'
        ? (dark ? 'text-red-400'     : 'text-red-600')
        : valueAccent === 'amber'
          ? (dark ? 'text-amber-400'   : 'text-amber-600')
          : valueAccent === 'sky'
            ? (dark ? 'text-sky-400'     : 'text-sky-600')
            : valueAccent === 'emerald'
              ? (dark ? 'text-emerald-400' : 'text-emerald-600')
              : (dark ? 'text-slate-50'    : 'text-slate-900')

  return (
    <div className={`flex-1 min-w-[140px] rounded-xl border overflow-hidden shadow-sm ${cardCls}`}>
      <div className="px-4 pt-4 pb-2 flex flex-col gap-1">
        <div className={`text-[11px] font-semibold uppercase tracking-widest ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {label}
        </div>
        <div className={`text-[26px] font-black tabular-nums leading-none ${valueCls}`}>
          {value}
        </div>
        <div className={`text-[11px] leading-tight ${dark ? 'text-slate-500' : 'text-slate-500'}`}>
          {sub}
        </div>
      </div>
      {progress !== undefined && (
        <div className="px-4 pb-3">
          <div className={`h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-100'}`}>
            <div
              className={`h-full ${progressColor ?? 'bg-sky-400'} rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          {/* Single right-justified label — the "0" left-anchor adds noise without value */}
          <div className={`text-right text-[10px] mt-1 font-semibold ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
            {progress}%
          </div>
        </div>
      )}
    </div>
  )
}
